import { useMemo, useCallback } from "react";
import { MONTH_LABELS, formatWon, calcDeltaPercent, formatDelta } from "Components/Sales/salesFormat";
import { buildCsv, downloadCsvFile } from "utils/csv";

export const buildTrendRows = (months, compare, throughMonth, { inProgressMonth = null } = {}) => {
  const byMonth = new Map((months || []).map((m) => [m.month, m]));
  const cmpByMonth = new Map((compare?.months || []).map((m) => [m.month, m]));
  return MONTH_LABELS.map((label, i) => {
    const monthNo = i + 1;
    const m = byMonth.get(monthNo) || null;
    const c = cmpByMonth.get(monthNo) || null;
    const inProgress = inProgressMonth === monthNo;
    const canDelta = m && c && !inProgress;
    return {
      month: monthNo,
      label,
      inProgress,
      gross: m ? m.gross : null,
      refund: m ? m.refund ?? 0 : null,
      net: m ? m.net : null,
      count: m ? m.count : null,
      refundRate: m ? m.refund_rate ?? 0 : null,
      avgTicket: m ? m.avg_ticket ?? 0 : null,
      compareGross: c ? c.gross : null,
      compareNet: c ? c.net : null,
      grossDelta: canDelta ? calcDeltaPercent(m.gross, c.gross) : null,
      netDelta: canDelta ? calcDeltaPercent(m.net, c.net) : null,
    };
  });
};

const sumField = (rows, key) => rows.reduce((acc, r) => acc + (r[key] || 0), 0);

const styles = {
  card: {
    backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb",
    borderRadius: "12px",
    overflow: "hidden",
    marginBottom: "20px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 20px",
    borderBottom: "1px solid #e5e7eb",
  },
  headTitle: { fontSize: "15px", fontWeight: "600", color: "#1f2937" },
  csvBtn: {
    padding: "7px 12px",
    borderRadius: "8px",
    border: "1px solid #d1d5db",
    backgroundColor: "#ffffff",
    fontSize: "13px",
    cursor: "pointer",
    color: "#4b5563",
    fontFamily: "inherit",
  },
  wrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", fontVariantNumeric: "tabular-nums" },
  th: {
    textAlign: "right",
    padding: "10px 12px",
    fontSize: "12px",
    color: "#6b7280",
    fontWeight: "600",
    backgroundColor: "#f8fafc",
    borderBottom: "2px solid #e5e7eb",
    whiteSpace: "nowrap",
  },
  thLeft: { textAlign: "left" },
  td: { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", textAlign: "right", color: "#1f2937" },
  tdLeft: { textAlign: "left" },
  muted: { color: "#9ca3af" },
  green: { color: "#059669" },
  red: { color: "#dc2626" },
  total: { fontWeight: "700", backgroundColor: "#f8fafc" },
};

const Dash = () => <span style={styles.muted}>-</span>;

const DeltaCell = ({ value }) => {
  if (value === null || value === undefined) return <Dash />;
  return <span style={value >= 0 ? styles.green : styles.red}>{formatDelta(value)}</span>;
};

const SalesTrendTable = ({ year, months, compare, throughMonth, inProgressMonth = null }) => {
  const rows = useMemo(
    () => buildTrendRows(months, compare, throughMonth, { inProgressMonth }),
    [months, compare, throughMonth, inProgressMonth]
  );
  const compareLabel = compare ? String(compare.year) : "비교";

  const totals = useMemo(() => {
    const gross = sumField(rows, "gross");
    const net = sumField(rows, "net");
    const count = sumField(rows, "count");
    const refund = sumField(rows, "refund");
    const ytdRows = rows.slice(0, throughMonth);
    const ytdGross = sumField(ytdRows, "gross");
    const ytdNet = sumField(ytdRows, "net");
    const cmpYtdGross = compare ? sumField(ytdRows, "compareGross") : null;
    const cmpYtdNet = compare ? sumField(ytdRows, "compareNet") : null;
    return {
      gross,
      net,
      count,
      compareGross: compare ? sumField(rows, "compareGross") : null,
      compareNet: compare ? sumField(rows, "compareNet") : null,
      grossDelta: compare ? calcDeltaPercent(ytdGross, cmpYtdGross) : null,
      netDelta: compare ? calcDeltaPercent(ytdNet, cmpYtdNet) : null,
      refundRate: gross ? (refund / gross) * 100 : 0,
      avgTicket: count ? Math.round(gross / count) : 0,
    };
  }, [rows, compare, throughMonth]);

  const handleCsv = useCallback(() => {
    const headers = [
      "월", "매출", `${compareLabel} 매출`, "매출 증감(%)",
      "순매출", `${compareLabel} 순매출`, "순매출 증감(%)",
      "건수", "환불률(%)", "객단가",
    ];
    const pct = (v) => (v === null || v === undefined ? "" : v.toFixed(1));
    const num = (v) => (v === null || v === undefined ? "" : v);
    const body = rows
      .filter((r) => r.gross !== null || r.compareGross !== null)
      .map((r) => [
        r.label, num(r.gross), num(r.compareGross), pct(r.grossDelta),
        num(r.net), num(r.compareNet), pct(r.netDelta),
        num(r.count), num(r.refundRate), num(r.avgTicket),
      ]);
    const suffix = compare ? `_vs_${compare.year}` : "";
    downloadCsvFile(buildCsv(headers, body), `매출추이_${year}${suffix}.csv`);
  }, [rows, compare, compareLabel, year]);

  return (
    <div style={styles.card}>
      <div style={styles.head}>
        <span style={styles.headTitle}>{year}년 월별 상세</span>
        <button type="button" style={styles.csvBtn} onClick={handleCsv}>CSV 다운로드</button>
      </div>
      <div style={styles.wrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{ ...styles.th, ...styles.thLeft }}>월</th>
              <th style={styles.th}>매출</th>
              <th style={styles.th}>{compareLabel} 매출</th>
              <th style={styles.th}>증감</th>
              <th style={styles.th}>순매출</th>
              <th style={styles.th}>{compareLabel} 순매출</th>
              <th style={styles.th}>증감</th>
              <th style={styles.th}>건수</th>
              <th style={styles.th}>환불률</th>
              <th style={styles.th}>객단가</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month}>
                <td style={{ ...styles.td, ...styles.tdLeft }}>
                  {r.label}{r.inProgress && <span style={styles.muted}> (진행 중)</span>}
                </td>
                <td style={styles.td}>{r.gross === null ? <Dash /> : formatWon(r.gross)}</td>
                <td style={{ ...styles.td, ...styles.muted }}>{r.compareGross === null ? <Dash /> : formatWon(r.compareGross)}</td>
                <td style={styles.td}><DeltaCell value={r.grossDelta} /></td>
                <td style={{ ...styles.td, ...styles.green }}>{r.net === null ? <Dash /> : formatWon(r.net)}</td>
                <td style={{ ...styles.td, ...styles.muted }}>{r.compareNet === null ? <Dash /> : formatWon(r.compareNet)}</td>
                <td style={styles.td}><DeltaCell value={r.netDelta} /></td>
                <td style={styles.td}>{r.count === null ? <Dash /> : `${r.count}건`}</td>
                <td style={styles.td}>
                  {r.refundRate === null ? <Dash /> : (
                    <span style={r.refundRate > 0 ? styles.red : undefined}>{Number(r.refundRate).toFixed(1)}%</span>
                  )}
                </td>
                <td style={styles.td}>{r.avgTicket === null ? <Dash /> : formatWon(r.avgTicket)}</td>
              </tr>
            ))}
            <tr style={styles.total}>
              <td style={{ ...styles.td, ...styles.tdLeft }}>합계</td>
              <td style={styles.td}>{formatWon(totals.gross)}</td>
              <td style={{ ...styles.td, ...styles.muted }}>{totals.compareGross === null ? <Dash /> : formatWon(totals.compareGross)}</td>
              <td style={styles.td}>{totals.grossDelta === null ? <Dash /> : <span style={styles.muted}>동기 {formatDelta(totals.grossDelta)}</span>}</td>
              <td style={{ ...styles.td, ...styles.green }}>{formatWon(totals.net)}</td>
              <td style={{ ...styles.td, ...styles.muted }}>{totals.compareNet === null ? <Dash /> : formatWon(totals.compareNet)}</td>
              <td style={styles.td}>{totals.netDelta === null ? <Dash /> : <span style={styles.muted}>동기 {formatDelta(totals.netDelta)}</span>}</td>
              <td style={styles.td}>{totals.count}건</td>
              <td style={styles.td}>{totals.refundRate.toFixed(1)}%</td>
              <td style={styles.td}>{formatWon(totals.avgTicket)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SalesTrendTable;
